import React, { useState, useEffect } from 'react';
import { paymentApi, DEFAULT_CLIENT_PAYMENT_SETTINGS } from '../../services/paymentApi';
import { PaymentSettings, WalletProviderType } from '../../types';
import {
  Save,
  RefreshCw,
  Phone,
  CreditCard,
  Building2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';

const WALLET_OPTIONS: { id: WalletProviderType; labelAr: string }[] = [
  { id: 'VODAFONE_CASH', labelAr: 'فودافون كاش (Vodafone Cash)' },
  { id: 'ORANGE_CASH', labelAr: 'أورنج كاش (Orange Cash)' },
  { id: 'ETISALAT_CASH', labelAr: 'اتصالات كاش (Etisalat Cash)' },
  { id: 'WE_PAY', labelAr: 'وي باي (WE Pay)' },
];

export const AdminPaymentSettings: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULT_CLIENT_PAYMENT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(
    null
  );

  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const res = await paymentApi.getPaymentSettings();
        if (res.success && res.data) {
          setSettings(res.data);
        }
      } catch (err: any) {
        console.warn('Using default settings fallback:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  const handleToggleProvider = (providerId: WalletProviderType) => {
    const current = settings.walletProviders || [];
    let updated: WalletProviderType[];
    if (current.includes(providerId)) {
      if (current.length === 1) {
        // Must keep at least one provider if wallet is enabled
        setFeedback({
          type: 'error',
          message: 'يجب اختيار مزود محفظة واحد على الأقل طالما أن خدمة المحافظ مفعلة.',
        });
        return;
      }
      updated = current.filter((p) => p !== providerId);
    } else {
      updated = [...current, providerId];
    }
    setSettings((prev) => ({ ...prev, walletProviders: updated }));
  };

  const handleTogglePaymentMethod = async (
    method: 'instapayEnabled' | 'walletEnabled' | 'branchPaymentEnabled',
    enabled: boolean
  ) => {
    // Validation before enabling
    if (enabled) {
      if (
        method === 'instapayEnabled' &&
        (!settings.instapayNumber?.trim() || !settings.instapayRecipient?.trim())
      ) {
        setFeedback({
          type: 'error',
          message: 'يرجى إدخال اسم المستفيد ورقم/عنوان إنستاباي أولاً لتفعيل الطريقة للطلاب.',
        });
        return;
      }
      if (
        method === 'walletEnabled' &&
        (!settings.walletNumber?.trim() || !settings.walletRecipient?.trim())
      ) {
        setFeedback({
          type: 'error',
          message: 'يرجى إدخال اسم المستفيد ورقم المحفظة أولاً لتفعيل المحافظ للطلاب.',
        });
        return;
      }
      if (
        method === 'branchPaymentEnabled' &&
        (!settings.branchName?.trim() || !settings.branchAddress?.trim())
      ) {
        setFeedback({
          type: 'error',
          message: 'يرجى إدخال اسم الفرع وعنوانه بالتفصيل أولاً لتفعيل الدفع من الفرع للطلاب.',
        });
        return;
      }
    }

    const updated = { ...settings, [method]: enabled };
    setSettings(updated);
    setIsSaving(true);
    setFeedback(null);

    try {
      const res = await paymentApi.updatePaymentSettings(updated);
      if (res.success) {
        setFeedback({
          type: 'success',
          message: enabled
            ? '✓ تم تفعيل وسيلة الدفع وإظهارها فوراً في واجهة حجز وطلب الطلاب!'
            : '✓ تم إيقاف وسيلة الدفع وإخفاؤها فوراً من واجهة حجز وطلب الطلاب!',
        });
        setTimeout(() => setFeedback(null), 4000);
      } else {
        setFeedback({
          type: 'error',
          message: res.error?.messageAr || 'حدث خطأ أثناء حفظ حالة وسيلة الدفع.',
        });
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err?.message || 'تعذر الاتصال بقاعدة البيانات لحفظ التعديل.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setFeedback(null);

    // Validation
    if (settings.instapayEnabled && (!settings.instapayNumber || !settings.instapayRecipient)) {
      setFeedback({
        type: 'error',
        message: 'بيانات إنستاباي غير مكتملة (رقم الحساب واسم المستفيد إلزاميان عند التفعيل).',
      });
      setIsSaving(false);
      return;
    }

    if (settings.walletEnabled && (!settings.walletNumber || !settings.walletRecipient)) {
      setFeedback({
        type: 'error',
        message: 'بيانات المحفظة غير مكتملة (رقم المحفظة واسم المستفيد إلزاميان عند التفعيل).',
      });
      setIsSaving(false);
      return;
    }

    if (settings.branchPaymentEnabled && (!settings.branchName || !settings.branchAddress)) {
      setFeedback({
        type: 'error',
        message: 'بيانات الفرع غير مكتملة (اسم الفرع والعنوان إلزاميان عند التفعيل).',
      });
      setIsSaving(false);
      return;
    }

    try {
      const res = await paymentApi.updatePaymentSettings(settings);
      if (res.success) {
        setFeedback({
          type: 'success',
          message: 'تم حفظ إعدادات الدفع وتحديثها بنجاح لجميع الطلاب فوراً!',
        });
        setTimeout(() => setFeedback(null), 5000);
      } else {
        setFeedback({
          type: 'error',
          message: res.error?.messageAr || 'حدث خطأ أثناء حفظ الإعدادات على الخادم.',
        });
      }
    } catch (err: any) {
      setFeedback({
        type: 'error',
        message: err?.message || 'تعذر الاتصال بالخادم لحفظ الإعدادات.',
      });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="admin-loading-container" dir="rtl">
        <RefreshCw size={32} className="animate-spin text-[#E5232A]" />
        <p>جاري تحميل إعدادات الدفع من الخادم...</p>
      </div>
    );
  }

  return (
    <div className="admin-settings-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h2 className="admin-page-title">إعدادات وسائل الدفع والتحويل</h2>
          <p className="admin-page-subtitle">
            التحكم في أرقام وحسابات الاستلام لطرق الدفع اليدوية (إنستاباي، المحافظ، الفرع) التي تظهر للطلاب في واجهة الحجز.
          </p>
        </div>
      </div>

      {/* Feedback Banner */}
      {feedback && (
        <div className={`admin-alert-banner ${feedback.type}`} role="alert">
          {feedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertTriangle size={18} />}
          <span>{feedback.message}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSave} className="admin-settings-form">
        {/* CARD 1: INSTAPAY */}
        <div className={`settings-card ${settings.instapayEnabled ? 'active-card' : 'disabled-card'}`}>
          <div className="settings-card-header">
            <div className="settings-card-title flex items-center gap-2">
              <Phone size={22} className="text-purple-600" />
              <div>
                <h3>تحويل إنستاباي (InstaPay)</h3>
                <span className="text-xs text-slate-500">التحويل اللحظي لحساب OTO البنكي أو عنوان الدفع (IPA)</span>
              </div>
            </div>

            <label className="switch-toggle-label" title={settings.instapayEnabled ? 'تعطيل إنستاباي' : 'تفعيل إنستاباي'}>
              <input
                type="checkbox"
                checked={settings.instapayEnabled}
                onChange={(e) => handleTogglePaymentMethod('instapayEnabled', e.target.checked)}
                disabled={isSaving}
              />
              <span className="switch-slider"></span>
              <span className="toggle-text">
                {settings.instapayEnabled ? 'مفعل للطلاب' : 'معطل مؤقتًا'}
              </span>
            </label>
          </div>

          <div className="settings-card-body">
            <div className="settings-grid">
              <div className="form-group">
                <label className="form-label">
                  اسم المستفيد الرسمي (كما يظهر في إنستاباي) <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.instapayRecipient}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, instapayRecipient: e.target.value }))
                  }
                  placeholder="مثال: OTO STUDENTS أو اسم الشركة"
                  disabled={!settings.instapayEnabled}
                  required={settings.instapayEnabled}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  رقم الهاتف أو عنوان الدفع اللحظي (IPA) <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="admin-input font-mono"
                  value={settings.instapayNumber}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, instapayNumber: e.target.value }))
                  }
                  placeholder="010XXXXXXXX أو username@instapay"
                  disabled={!settings.instapayEnabled}
                  required={settings.instapayEnabled}
                />
              </div>
            </div>

            <div className="form-group mt-3">
              <label className="form-label">تعليمات التحويل للطلاب</label>
              <textarea
                className="admin-textarea"
                rows={2}
                value={settings.instapayInstructions}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, instapayInstructions: e.target.value }))
                }
                placeholder="تعليمات تظهر للطالب أثناء التحويل..."
                disabled={!settings.instapayEnabled}
              />
            </div>
          </div>
        </div>

        {/* CARD 2: MOBILE WALLET */}
        <div className={`settings-card ${settings.walletEnabled ? 'active-card' : 'disabled-card'}`}>
          <div className="settings-card-header">
            <div className="settings-card-title flex items-center gap-2">
              <CreditCard size={22} className="text-emerald-600" />
              <div>
                <h3>المحافظ الإلكترونية (Mobile Wallets)</h3>
                <span className="text-xs text-slate-500">فودافون كاش، أورنج كاش، اتصالات كاش، وي باي</span>
              </div>
            </div>

            <label className="switch-toggle-label" title={settings.walletEnabled ? 'تعطيل المحافظ' : 'تفعيل المحافظ'}>
              <input
                type="checkbox"
                checked={settings.walletEnabled}
                onChange={(e) => handleTogglePaymentMethod('walletEnabled', e.target.checked)}
                disabled={isSaving}
              />
              <span className="switch-slider"></span>
              <span className="toggle-text">
                {settings.walletEnabled ? 'مفعل للطلاب' : 'معطل مؤقتًا'}
              </span>
            </label>
          </div>

          <div className="settings-card-body">
            {/* Active Wallet Providers Checkboxes */}
            <div className="form-group">
              <label className="form-label">المحافظ المقبولة حالياً:</label>
              <div className="providers-checkboxes-grid">
                {WALLET_OPTIONS.map((provider) => {
                  const isChecked = (settings.walletProviders || []).includes(provider.id);
                  return (
                    <label key={provider.id} className="provider-check-box">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => handleToggleProvider(provider.id)}
                        disabled={!settings.walletEnabled}
                      />
                      <span>{provider.labelAr}</span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="settings-grid mt-3">
              <div className="form-group">
                <label className="form-label">
                  رقم المحفظة الموحد للتحويل <span className="req">*</span>
                </label>
                <input
                  type="tel"
                  className="admin-input font-mono"
                  value={settings.walletNumber}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, walletNumber: e.target.value }))
                  }
                  placeholder="010XXXXXXXX"
                  disabled={!settings.walletEnabled}
                  required={settings.walletEnabled}
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  اسم المستفيد المسجل للمحفظة <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.walletRecipient}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, walletRecipient: e.target.value }))
                  }
                  placeholder="مثال: OTO STUDENTS"
                  disabled={!settings.walletEnabled}
                  required={settings.walletEnabled}
                />
              </div>
            </div>

            <div className="form-group mt-3">
              <label className="form-label">تعليمات سداد المحفظة للطلاب</label>
              <textarea
                className="admin-textarea"
                rows={2}
                value={settings.walletInstructions}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, walletInstructions: e.target.value }))
                }
                placeholder="تعليمات تظهر للطالب..."
                disabled={!settings.walletEnabled}
              />
            </div>
          </div>
        </div>

        {/* CARD 3: PAY AT BRANCH */}
        <div
          className={`settings-card ${
            settings.branchPaymentEnabled ? 'active-card' : 'disabled-card'
          }`}
        >
          <div className="settings-card-header">
            <div className="settings-card-title flex items-center gap-2">
              <Building2 size={22} className="text-[#E5232A]" />
              <div>
                <h3>الدفع المباشر من الفرع (Pay at Branch)</h3>
                <span className="text-xs text-slate-500">سداد نقدي بمكتب الإسكندرية واعتماد يدوي</span>
              </div>
            </div>

            <label className="switch-toggle-label" title={settings.branchPaymentEnabled ? 'تعطيل الدفع بالفرع' : 'تفعيل الدفع بالفرع'}>
              <input
                type="checkbox"
                checked={settings.branchPaymentEnabled}
                onChange={(e) => handleTogglePaymentMethod('branchPaymentEnabled', e.target.checked)}
                disabled={isSaving}
              />
              <span className="switch-slider"></span>
              <span className="toggle-text">
                {settings.branchPaymentEnabled ? 'مفعل للطلاب' : 'معطل مؤقتًا'}
              </span>
            </label>
          </div>

          <div className="settings-card-body">
            <div className="settings-grid">
              <div className="form-group">
                <label className="form-label">
                  اسم الفرع أو المكتب <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="admin-input"
                  value={settings.branchName}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, branchName: e.target.value }))
                  }
                  placeholder="مكتب OTO - فرع سيدي جابر"
                  disabled={!settings.branchPaymentEnabled}
                  required={settings.branchPaymentEnabled}
                />
              </div>

              <div className="form-group">
                <label className="form-label">هاتف التواصل للفرع</label>
                <input
                  type="tel"
                  className="admin-input font-mono"
                  value={settings.branchPhone || ''}
                  onChange={(e) =>
                    setSettings((prev) => ({ ...prev, branchPhone: e.target.value }))
                  }
                  placeholder="010XXXXXXXX"
                  disabled={!settings.branchPaymentEnabled}
                />
              </div>
            </div>

            <div className="form-group mt-3">
              <label className="form-label">
                عنوان الفرع بالتفصيل <span className="req">*</span>
              </label>
              <input
                type="text"
                className="admin-input"
                value={settings.branchAddress}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, branchAddress: e.target.value }))
                }
                placeholder="الإسكندرية، محطة سيدي جابر، شارع المشير..."
                disabled={!settings.branchPaymentEnabled}
                required={settings.branchPaymentEnabled}
              />
            </div>

            <div className="form-group mt-3">
              <label className="form-label">مواعيد العمل الرسمية</label>
              <input
                type="text"
                className="admin-input"
                value={settings.branchWorkingHours || ''}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, branchWorkingHours: e.target.value }))
                }
                placeholder="يوميًا من 8:00 صباحًا حتى 6:00 مساءً"
                disabled={!settings.branchPaymentEnabled}
              />
            </div>

            <div className="form-group mt-3">
              <label className="form-label">تعليمات سداد الفرع للطلاب</label>
              <textarea
                className="admin-textarea"
                rows={2}
                value={settings.branchInstructions}
                onChange={(e) =>
                  setSettings((prev) => ({ ...prev, branchInstructions: e.target.value }))
                }
                placeholder="تعليمات الحضور للفرع..."
                disabled={!settings.branchPaymentEnabled}
              />
            </div>
          </div>
        </div>

        {/* Security / Audit Note */}
        <div className="security-notice-callout">
          <AlertTriangle size={18} className="text-[#E5232A] flex-shrink-0" />
          <p>
            تغيير أرقام الدفع يتم حفظه ومزامنته فورياً مع جميع الطلاب. الطلبات السابقة تحتفظ بنسخة مطابقة (Destination Snapshot) من الحساب الذي تم التحويل إليه لضمان النزاهة والمطابقة المالية.
          </p>
        </div>

        {/* Submit Actions Row */}
        <div className="form-actions-row">
          <button
            type="submit"
            className="admin-btn-save flex items-center gap-2"
            disabled={isSaving}
          >
            <Save size={18} />
            <span>{isSaving ? 'جاري حفظ التغييرات...' : 'حفظ ونشر إعدادات الدفع'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminPaymentSettings;
